import React from 'react';
import Badge from '.';
import { SKIN, TYPE, SIZE } from './Badge.constants';
import {
  createRendererWithUniDriver,
  cleanup,
  fireEvent,
} from '../utils/test-utils/unit';
import { badgeUniDriverFactory } from './Badge.uni.driver';
import { BadgeSize, BadgeSkin, BadgeType } from './Badge.types';

describe('Badge', () => {
  const render = createRendererWithUniDriver(badgeUniDriverFactory);
  const createDriver = (jsx: React.ReactElement) => render(jsx).driver;

  afterEach(() => cleanup());

  describe('type prop', () => {
    it('should be solid by default', async () => {
      const driver = createDriver(<Badge>Hello</Badge>);
      expect(await driver.getType()).toBe(TYPE.solid);
    });

    Object.keys(TYPE).forEach(type => {
      it(`should be ${type}`, async () => {
        const driver = createDriver(
          <Badge type={type as BadgeType}>Hello</Badge>,
        );
        expect(await driver.getType()).toBe(type);
      });
    });

    it('should use default value when value is provided as undefined', async () => {
      const driver = createDriver(<Badge type={undefined}>Hello</Badge>);
      expect(await driver.getType()).toBe(TYPE.solid);
    });
  });

  describe('skin prop', () => {
    it('should be general by default', async () => {
      const driver = createDriver(<Badge>Hello</Badge>);
      expect(await driver.getSkin()).toBe(SKIN.general);
    });

    Object.keys(SKIN).forEach(skin => {
      it(`should be ${skin}`, async () => {
        const driver = createDriver(
          <Badge skin={skin as BadgeSkin}>Hello</Badge>,
        );
        expect(await driver.getSkin()).toBe(skin);
      });
    });
  });

  describe('uppercase prop', () => {
    it('should be uppercase by default', async () => {
      const driver = createDriver(<Badge>Hello</Badge>);
      expect(await driver.isUppercase()).toBe(true);
    });

    it('should be free-case when value is false', async () => {
      const driver = createDriver(<Badge uppercase={false}>Hello</Badge>);
      expect(await driver.isUppercase()).toBe(false);
    });
  });

  describe('size prop', () => {
    it('should be medium by default', async () => {
      const driver = createDriver(<Badge>Hello</Badge>);
      expect(await driver.getSize()).toBe(SIZE.medium);
    });

    Object.keys(SIZE).forEach(size => {
      it(`should be ${size}`, async () => {
        const driver = createDriver(
          <Badge size={size as BadgeSize}>Hello</Badge>,
        );
        expect(await driver.getSize()).toBe(size);
      });
    });
  });

  describe('onClick prop', () => {
    it('cursor should be default when no onClick', async () => {
      const driver = createDriver(<Badge>Hello</Badge>);
      expect(await driver.hasClickCursor()).toBe(false);
    });

    it('cursor should be pointer when onClick set', async () => {
      const driver = createDriver(<Badge onClick={(e: any) => e}>Hello</Badge>);
      expect(await driver.hasClickCursor()).toBe(true);
    });

    it('should call event handler on badge click', async () => {
      const handler = jest.fn();
      const driver = createDriver(
        <Badge onClick={() => handler()}>Hello</Badge>,
      );
      await driver.click();
      expect(handler).toHaveBeenCalled();
    });

    it('should have tabIndex=0 if onClick passed', async () => {
      const driver = createDriver(<Badge onClick={() => {}}>Hello</Badge>);
      expect(await driver.base.attr('tabindex')).toBe('0');
    });

    it('should not have tabIndex if onClick is not passed', async () => {
      const driver = createDriver(<Badge>Hello</Badge>);
      expect(await driver.base.attr('tabindex')).toBeNull();
    });

    it('should have role="button" if onClick passed', async () => {
      const driver = createDriver(<Badge onClick={() => {}}>Hello</Badge>);
      expect(await driver.base.attr('role')).toBe('button');
    });

    it('should not have role if onClick is not passed', async () => {
      const driver = createDriver(<Badge>Hello</Badge>);
      expect(await driver.base.attr('role')).toBeNull();
    });

    it('should call onClick when Enter key is pressed', async () => {
      const onClick = jest.fn();
      const { container } = render(<Badge onClick={onClick}>Hello</Badge>);
      fireEvent.keyDown(container.firstElementChild!, { key: 'Enter' });
      expect(onClick).toHaveBeenCalledTimes(1);
    });

    it('should call onClick when Space key is pressed', async () => {
      const onClick = jest.fn();
      const { container } = render(<Badge onClick={onClick}>Hello</Badge>);
      fireEvent.keyDown(container.firstElementChild!, { key: ' ' });
      expect(onClick).toHaveBeenCalledTimes(1);
    });
  });

  describe('children prop', () => {
    it('should render the text given as a children prop', async () => {
      const driver = createDriver(<Badge>Hello</Badge>);
      expect(await driver.text()).toBe('Hello');
    });
  });
});
